// ============================================================================
// Invoicing — dashboard widget for finance / executive / superuser.
// Two clickable stats: unbilled (facilities + hours; $ unknown until rates are
// set on the contract) and open invoice drafts (count + booked $). Both deep-
// link into /invoicing. Self-fetching + RLS-scoped; gated by the parent.
// ============================================================================

import { Link } from 'react-router-dom';
import { ArrowUpRight, FileText, Receipt } from 'lucide-react';
import { WidgetCard, WidgetSkeleton, fmtCompactCurrency } from '../widget-card';
import { useInvoicingSummary } from '../../invoicing';

interface Props {
  enabled: boolean;
}

export function InvoicingWidget({ enabled }: Props) {
  const { data, isLoading, error } = useInvoicingSummary();
  if (!enabled) return null;

  return (
    <WidgetCard title="Invoicing" subtitle="Unbilled hours + open invoice drafts." minHeight={240}>
      {error && <p className="text-xs text-st-danger-fg">Couldn't load: {error}</p>}
      {!error && isLoading && <WidgetSkeleton lines={3} />}
      {!error && !isLoading && data && (
        <div className="-mx-2 divide-y divide-tcs-border/60">
          <Link
            to="/invoicing"
            className="group flex items-center justify-between gap-3 px-2 py-2.5 hover:bg-tcs-surface"
          >
            <span className="flex min-w-0 items-center gap-2 text-sm text-tcs-text">
              <Receipt size={14} className="shrink-0 text-st-ok-fg" aria-hidden />
              Ready to invoice
            </span>
            <span className="flex items-center gap-1 text-right">
              <span className="block">
                <span className="block tabular-nums font-semibold text-tcs-text">
                  {data.unbilledFacilities} facilities
                </span>
                <span className="block text-[11px] text-tcs-text-muted">
                  {data.unbilledHours.toLocaleString('en-US', { maximumFractionDigits: 0 })} hrs · $ pending rates
                </span>
              </span>
              <ArrowUpRight size={12} className="text-tcs-text-faint group-hover:text-tcs-text-secondary" aria-hidden />
            </span>
          </Link>
          <Link
            to="/invoicing"
            className="group flex items-center justify-between gap-3 px-2 py-2.5 hover:bg-tcs-surface"
          >
            <span className="flex min-w-0 items-center gap-2 text-sm text-tcs-text">
              <FileText size={14} className="shrink-0 text-st-ok-fg" aria-hidden />
              Open drafts
            </span>
            <span className="flex items-center gap-1 text-right">
              <span className="block">
                <span className="block tabular-nums font-semibold text-tcs-text">{data.draftCount}</span>
                <span className="block text-[11px] text-tcs-text-muted">{fmtCompactCurrency(data.draftValue)} booked</span>
              </span>
              <ArrowUpRight size={12} className="text-tcs-text-faint group-hover:text-tcs-text-secondary" aria-hidden />
            </span>
          </Link>
        </div>
      )}
    </WidgetCard>
  );
}
